// Cross-platform image saving helpers for the Social Media Hub.
//
// - iPhone / iPad: the Web Share API (navigator.share with files) opens the
//   native share sheet, which offers "Save Image" (camera roll / Photos) and
//   "Save to Files". A blob download (<a download>) saves straight into Files.
// - Android: the share sheet offers Gallery / Drive etc.; downloads land in
//   the Downloads folder (visible in the gallery app).
// - Desktop: files are downloaded with a proper filename.
//
// IMPORTANT (iOS): navigator.share must be called during a user tap. Safari
// rejects it if we `await fetch()` first, so images are PRE-FETCHED into File
// objects (prepareMediaFiles) when the save panel opens, and the share call is
// then made synchronously from the button's click handler.

export interface SaveableImage {
  id: string;
  url: string;
  /** Base filename without extension, e.g. "zera-monday-ab12cd". */
  baseName: string;
  label: string;
  sublabel?: string;
}

export interface PreparedImage extends SaveableImage {
  file: File | null;
  error?: boolean;
}

export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iPadOS = navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1;
  return /iPad|iPhone|iPod/.test(ua) || iPadOS;
}

export function isAndroid(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android/i.test(navigator.userAgent || '');
}

export function isMobileDevice(): boolean {
  return isIOS() || isAndroid();
}

const EXT_BY_TYPE: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/heic': 'heic',
};

function extFromUrl(url: string): string {
  if (url.startsWith('data:')) {
    const m = url.match(/^data:image\/([a-z0-9+]+)/i);
    return m ? (m[1] === 'jpeg' ? 'jpg' : m[1].toLowerCase()) : 'png';
  }
  const clean = url.split('?')[0].split('#')[0];
  const ext = clean.split('.').pop()?.toLowerCase() || '';
  return /^(jpg|jpeg|png|webp|gif|heic)$/.test(ext) ? (ext === 'jpeg' ? 'jpg' : ext) : 'jpg';
}

/** Fetch a remote/data URL into a File with a sensible name + MIME type. */
export async function urlToFile(url: string, baseName: string): Promise<File> {
  const res = await fetch(url, { mode: 'cors', cache: 'no-store' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const type = blob.type && blob.type.startsWith('image/') ? blob.type : `image/${extFromUrl(url) === 'jpg' ? 'jpeg' : extFromUrl(url)}`;
  const ext = EXT_BY_TYPE[type] || extFromUrl(url);
  return new File([blob], `${baseName}.${ext}`, { type, lastModified: Date.now() });
}

/** Pre-fetch every image so share/download can run synchronously on tap. */
export async function prepareMediaFiles(images: SaveableImage[]): Promise<PreparedImage[]> {
  return Promise.all(
    images.map(async (img) => {
      try {
        const file = await urlToFile(img.url, img.baseName);
        return { ...img, file };
      } catch {
        return { ...img, file: null, error: true };
      }
    }),
  );
}

/** True if the browser can open the native share sheet with these files. */
export function canShareFiles(files: File[]): boolean {
  if (typeof navigator === 'undefined' || !files.length) return false;
  const nav = navigator as any;
  if (typeof nav.share !== 'function' || typeof nav.canShare !== 'function') return false;
  try {
    return nav.canShare({ files });
  } catch {
    return false;
  }
}

export type ShareResult = 'shared' | 'cancelled' | 'unsupported' | 'failed';

/**
 * Open the native share sheet (iOS: "Save Image" -> Photos, "Save to Files").
 * Must be called directly from a click/tap handler.
 */
export async function shareFiles(files: File[], title?: string): Promise<ShareResult> {
  if (!canShareFiles(files)) return 'unsupported';
  try {
    await (navigator as any).share({ files, title: title || 'Post images' });
    return 'shared';
  } catch (err: any) {
    if (err && err.name === 'AbortError') return 'cancelled';
    return 'failed';
  }
}

/** Trigger a file download for a prepared File (desktop, Android, iOS -> Files app). */
export function downloadFile(file: File) {
  const objectUrl = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = file.name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
}

/** Download several files one after another (browsers throttle bursts). */
export async function downloadFilesSequentially(files: File[], gapMs = 450) {
  for (let i = 0; i < files.length; i++) {
    downloadFile(files[i]);
    if (i < files.length - 1) await new Promise((r) => setTimeout(r, gapMs));
  }
}

/** Last-resort fallback: open the raw image so the user can long-press / right-click save. */
export function openImageInNewTab(url: string) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

/** Filename-safe slug. */
export function slugify(s: string | null | undefined, fallback = 'post'): string {
  const v = (s || fallback).replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase();
  return v || fallback;
}
